import { IconButton } from '@components/core/icon-button.component';
import { useDataTableFilters } from '@components/data-table-filter/hooks/use-data-table-filters';
import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { useRequireAdmin } from '@hooks/use-require-admin.hook';
import { useAdminUsersQuery } from '@queries/admin-users.query';
import { RefreshCcwIcon } from 'lucide-react';
import React, { useEffect } from 'react';
import { columnsConfig } from './admin-users-columns.config';
import { AdminUsersModal } from './components/admin-users-modal.component';
import { AdminUsersPagination } from './components/admin-users-pagination.component';
import { AdminUsersTable } from './components/admin-users-table.component';
import { useAdminUsersActions } from './hooks/use-admin-users-actions.hook';
import { useAdminUsersQueryParams } from './hooks/use-admin-users-query-params.hook';

export const AdminUsersPage: React.FC = () => {
  const { isAdmin, isPending: isAdminPending } = useRequireAdmin();

  const {
    DEFAULT_SORTING,
    filtersState,
    handleLimitChange,
    page,
    queryParams,
    search,
    selectedUserId,
    setFiltersState,
    setPage,
    setSearch,
    setSelectedUserId,
    setSorting,
    sortingState,
  } = useAdminUsersQueryParams();

  const { data, isPending, isFetching, refetch: refetchUsers } = useAdminUsersQuery(queryParams);

  const users = data?.users ?? [];
  const pagination = data?.pagination;

  // Initialize data-table-filter hook
  const { columns, filters, actions, strategy } = useDataTableFilters({
    columnsConfig,
    data: users,
    filters: filtersState,
    onFiltersChange: setFiltersState,
    strategy: 'server',
  });

  // Handle page bounds
  useEffect(() => {
    if (!pagination) return;

    const current = page;
    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (current !== 1) void setPage(1);
      return;
    }

    if (current > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  // User action handlers
  const {
    handleApproveToggle,
    handleBlockToggle,
    handleMarkAsVerified,
    handleResendVerification,
    handleSetAccessWindow,
    handleUpdateUserFeatureFlags,
    loadingUserIds,
  } = useAdminUsersActions();

  if (isAdminPending || !isAdmin) {
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Admin Users" />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex flex-col w-full gap-6">
          <Tile
            header={{
              title: 'Users',
              actions: (
                <IconButton
                  ariaLabel="Refresh users"
                  disabled={isFetching}
                  icon={<RefreshCcwIcon />}
                  variant="secondary"
                  onClick={() => {
                    refetchUsers();
                  }}
                />
              ),
            }}
          >
            <div className="flex flex-col gap-4">
              <AdminUsersTable
                actions={actions}
                columns={columns}
                filters={filters}
                isLoading={isPending}
                onSearchChange={(value) => void setSearch(value)}
                onSelectUser={(userId) => void setSelectedUserId(userId)}
                onSortingChange={(state) => {
                  if (!state || state.length === 0) {
                    void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                    return;
                  }
                  void setSorting(state);
                }}
                search={search}
                sorting={sortingState}
                strategy={strategy}
                users={users}
              />

              {pagination && (
                <AdminUsersPagination
                  isFetching={isFetching}
                  pagination={pagination}
                  onPageChange={(nextPage) => void setPage(Math.max(1, nextPage))}
                  onPageSizeChange={handleLimitChange}
                />
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>

      <AdminUsersModal
        isBusy={selectedUserId ? Boolean(loadingUserIds[selectedUserId]) : false}
        isOpen={Boolean(selectedUserId)}
        onApproveToggle={(userId, nextValue) => void handleApproveToggle(userId, nextValue)}
        onBlockToggle={(userId, shouldBlock, reason) => void handleBlockToggle(userId, shouldBlock, reason)}
        onClose={() => void setSelectedUserId(null)}
        onMarkAsVerified={(userId) => void handleMarkAsVerified(userId)}
        onResendVerification={(userId) => void handleResendVerification(userId)}
        onSetAccessWindow={(userId, accessExpiredAt) => void handleSetAccessWindow(userId, accessExpiredAt)}
        onUpdateUserFeatureFlags={(userId, featureFlags) => void handleUpdateUserFeatureFlags(userId, featureFlags)}
        userId={selectedUserId ?? undefined}
      />
    </PageviewTracker>
  );
};
